import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './PerplexitySidebar.scss';
import { 
    CpuIcon, 
    DeepThinkIcon, 
    HandshakeIcon,
    HistoryIcon,
    NewsIcon,
    BlueprintIcon
} from 'components/Icons';
import EnkiDarkLogo from 'components/Image/EnkiDarkLogo';

interface SidebarItem {
    id: string;
    label: string;
    icon: React.ReactNode;
    links: { label: string; url: string }[];
}

const SidebarLinkItem: React.FC<{ label: string; url: string; onClick: () => void }> = ({ label, url, onClick }) => {
    const [displayLabel, setDisplayLabel] = useState(label || '');
    const [isLoading, setIsLoading] = useState(!label && !!url);

    React.useEffect(() => {
        if (label) {
            setDisplayLabel(label);
            setIsLoading(false);
        } else if (url) {
            setIsLoading(true);
            const fetchTitle = async () => {
                const formatUrlToTitle = (rawUrl: string) => {
                    const slug = rawUrl.split('/').filter(Boolean).pop() || rawUrl;
                    return slug
                        .replace(/[_-]/g, ' ')
                        .split(' ')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                        .join(' ');
                };

                try {
                    let host = process.env.REACT_APP_SERVER || '';
                    if (!host && window.location.hostname === 'localhost') {
                        host = 'http://localhost:5000';
                    }
                    
                    const response = await fetch(`${host}/api/metadata/title`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ url })
                    });

                    if (!response.ok) throw new Error('API Error');

                    const data = await response.json();
                    if (data.title && data.title !== '') {
                        setDisplayLabel(data.title);
                    } else {
                        setDisplayLabel(formatUrlToTitle(url));
                    }
                } catch (error) {
                    console.error('Error fetching title:', error);
                    setDisplayLabel(formatUrlToTitle(url));
                } finally {
                    setIsLoading(false);
                }
            };
            fetchTitle();
        }
    }, [label, url]);

    return (
        <a 
            href={url} 
            className={`perplexity-sidebar__link-item ${isLoading ? 'is-loading' : ''}`}
            onClick={onClick}
            target="_blank"
            rel="noopener noreferrer"
        >
            <i className="material-icons">{isLoading ? 'refresh' : 'article'}</i>
            <span>{isLoading ? 'Loading...' : displayLabel}</span>
        </a>
    );
};

interface PerplexitySidebarProps {
    onPinChange: (pinned: boolean) => void;
}

const PerplexitySidebar: React.FC<PerplexitySidebarProps> = ({ onPinChange }) => {
    const [activeMenu, setActiveMenu] = useState<string | null>(null);
    const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);
    const [isPinned, setIsPinned] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const sidebarRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        setSearchQuery('');
    }, [activeMenu]);

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
                if (!isPinned) {
                    setActiveMenu(null);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isPinned]);

    const handlePin = (e: React.MouseEvent) => {
        e.stopPropagation();
        const newPinned = !isPinned;
        
        if (newPinned) {
            // Check both hoveredMenu and activeMenu to capture what is currently shown
            const menuToKeep = hoveredMenu || activeMenu;
            if (menuToKeep) {
                setActiveMenu(menuToKeep);
            }
        } else {
            // When unpinning, we can clear the active menu if we are strictly leaving
            // But usually, we might want to keep it if hovering. 
            // For now, let's reset activeMenu if it was only held by pin logic? 
            // Actually, standard behavior is fine: if unpinned, onMouseLeave will handle closing.
        }

        setIsPinned(newPinned);
        onPinChange(newPinned);
    };

    const handleItemClick = (id: string) => {
        // If pinned, clicking an item should switch to it (which works via setActiveMenu)
        // If getting unselected?
        if (activeMenu === id && !isPinned) {
            setActiveMenu(null);
        } else {
            setActiveMenu(id);
        }
    };

    const menuItems: SidebarItem[] = [
        {
            id: 'sofc',
            label: 'SOFC',
            icon: <HandshakeIcon />,
            links: [
                { label: 'SOFC Industry Analysis 2025: Green Maritime Leaders - EnkiAI', url: 'https://enkiai.com/solid-oxide-fuel-cells/sofc-industry-analysis-2025-green-maritime-leaders' },
                { label: 'SOFC Industry Analysis 2025: Top Solid Oxide Fuel Cells - EnkiAI', url: 'https://enkiai.com/solid-oxide-fuel-cells/sofc-green-hydrogen-market-analysis-2025-future-trends' },
                { label: 'Solid Oxide Fuel Cells in Shipping: 2026 Market Analysis, Trends & Leaders', url: 'https://enkiai.com/solid-oxide-fuel-cells/sofc-in-maritime-shipping-2026-market-outlook/' },
            ]
        },
        {
            id: 'data-center',
            label: 'Data Center',
            icon: <CpuIcon />,
            links: [
                { label: 'Liquid Cooling Data Centers: AI Infrastructure Trends 2025 - EnkiAI', url: 'https://enkiai.com/data-center/liquid-cooling-for-ai-data-centers-2025-trends-tech' },
            ]
        },
        {
            id: 'dac',
            label: 'DAC',
            icon: <DeepThinkIcon />,
            links: [
                { label: 'Direct Air Carbon Capture Market Analysis: Growth, Confidence, and Market Reality(2023-2025) - EnkiAI', url: 'https://enkiai.com/direct-air-carbon-capture-market-analysis-growth-confidence-and-market-reality-2023-2025' },
                { label: 'SOFC Industry Analysis 2025: Green Maritime Leaders', url: 'https://enkiai.com/solid-oxide-fuel-cells/sofc-industry-analysis-2025-green-maritime-leaders' }
            ]
        }
    ];

    const currentMenu = activeMenu || hoveredMenu;
    const activeItem = menuItems.find(i => i.id === currentMenu);

    const getSearchText = (link: { label: string; url: string }) => (link.label || link.url || '').toLowerCase();
    const normalizedQuery = searchQuery.toLowerCase();

    const filteredLinks = activeItem?.links.filter(link => 
        getSearchText(link).includes(normalizedQuery)
    ) || [];

    return (
        <div 
            className={`perplexity-sidebar-wrapper ${isPinned ? 'is-pinned' : ''}`} 
            ref={sidebarRef}
            onMouseLeave={() => setHoveredMenu(null)}
        >
            <aside className="perplexity-sidebar">
                <div className="perplexity-sidebar__top">
                    <div className="perplexity-sidebar__logo">
                        <EnkiDarkLogo />
                    </div>
                </div>
                
                <nav className="perplexity-sidebar__nav">
                    {menuItems.map((item) => (
                        <div 
                            key={item.id}
                            className={`perplexity-sidebar__nav-item ${activeMenu === item.id ? 'active' : ''}`}
                            onClick={() => handleItemClick(item.id)}
                            onMouseEnter={() => !activeMenu && setHoveredMenu(item.id)}
                        >
                            <div className="perplexity-sidebar__item-icon">
                                {item.icon}
                            </div>
                            <span className="perplexity-sidebar__item-label">{item.label}</span>
                        </div>
                    ))}
                </nav>

                <div className="perplexity-sidebar__bottom">
                    {/* Additional bottom items can go here */}
                </div>
            </aside>

            {/* Flyout Submenu */}
            {activeItem && (
                <div className={`perplexity-sidebar__flyout ${(activeMenu || hoveredMenu) ? 'open' : ''}`}>
                    <div className="perplexity-sidebar__flyout-header">
                        <h3>{activeItem.label.replace('\n', '')}</h3>
                        <button className={`perplexity-sidebar__pin-btn ${isPinned ? 'pinned' : ''}`} onClick={handlePin}>
                            <i className="material-icons">{isPinned ? 'push_pin' : 'push_pin'}</i>
                        </button>
                    </div>

                    <div className="perplexity-sidebar__search-container">
                        <i className="material-icons">search</i>
                        <input 
                            type="text" 
                            placeholder="Search topics..." 
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            autoComplete="off"
                        />
                    </div>

                    <div className="perplexity-sidebar__flyout-content">
                        {filteredLinks.map((link) => (
                            <SidebarLinkItem 
                                key={link.url}
                                label={link.label}
                                url={link.url}
                                onClick={() => !isPinned && setActiveMenu(null)}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>

    );
};

export default PerplexitySidebar;
